import { useRef, useState, type ReactNode } from 'react';
import { Camera, ImagePlus, Trash2 } from 'lucide-react';
import { cx, Spinner } from './ui';
import { openContextMenu } from './ContextMenu';
import { squareImage, uploadImage } from '@/lib/images';
import { errorMessage, toast } from '@/lib/toast';

/**
 * Click (or drop an image) to upload a square picture: avatars and logos.
 * The image is cropped and resized in the browser, uploaded to `endpoint`
 * (/api/images/...), and the resulting URL handed to `onChange`, which
 * attaches it with a reducer. `onChange('')` removes it.
 */
export function ImagePicker({
  url,
  endpoint,
  size = 72,
  maxPx,
  round = false,
  fallback,
  disabled = false,
  label = 'image',
  showRemove = true,
  onChange,
}: {
  url: string;
  endpoint: string;
  size?: number;
  /** Stored image size in px (square). */
  maxPx: number;
  round?: boolean;
  fallback: ReactNode;
  disabled?: boolean;
  /** Used in messages: "avatar", "logo". */
  label?: string;
  /** Show a "Remove" link under the image (it is always in the right-click menu). */
  showRemove?: boolean;
  onChange: (url: string) => Promise<unknown> | unknown;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);

  const upload = async (file: File | undefined) => {
    if (!file || disabled) return;
    setBusy(true);
    try {
      const blob = await squareImage(file, maxPx);
      const next = await uploadImage(endpoint, blob);
      await onChange(next);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await onChange('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cx('inline-flex flex-col items-center', showRemove && 'gap-1.5')}>
      <button
        type="button"
        disabled={disabled || busy}
        title={disabled ? undefined : `Change ${label}`}
        onClick={() => input.current?.click()}
        onContextMenu={(e) =>
          !disabled &&
          openContextMenu(e, [
            { label: `Upload ${label}…`, icon: <ImagePlus size={14} />, onClick: () => input.current?.click() },
            url ? { label: `Remove ${label}`, icon: <Trash2 size={14} />, danger: true, onClick: () => void remove() } : null,
          ])
        }
        onDragOver={(e) => {
          if (disabled || !e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          void upload(e.dataTransfer.files[0]);
        }}
        className={cx(
          'group relative shrink-0 overflow-hidden bg-panel-3 outline-none ring-accent focus-visible:ring-2',
          round ? 'rounded-full' : size < 40 ? 'rounded-md' : 'rounded-xl',
          over && 'ring-2',
          !disabled && 'cursor-pointer',
        )}
        style={{ width: size, height: size }}
      >
        {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center">{fallback}</span>}
        {!disabled ? (
          <span
            className={cx(
              'absolute inset-0 flex items-center justify-center bg-black/55 text-white transition-opacity',
              busy || over ? 'opacity-100' : 'picker-overlay opacity-0 group-hover:opacity-100',
            )}
          >
            {busy ? <Spinner size={18} /> : <Camera size={Math.max(14, size / 4)} />}
          </span>
        ) : null}
      </button>
      {!disabled && url && showRemove ? (
        <button type="button" className="text-[11px] text-faint hover:text-danger" disabled={busy} onClick={() => void remove()}>
          Remove
        </button>
      ) : null}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        hidden
        onChange={(e) => {
          void upload(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
